var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import styled, { css } from 'styled-components';
import { margin } from 'styled-system';
import { muiSuccessFill, muiErrorFill, muiInfoFill, muiLightbulbInsights, } from '@ally/metronome-icons';
import { Icon } from '../Icon/Icon';
import { TextBody } from '../Typography';
import { get, useFocusOnMount } from '../tools/utils';
import { Card } from '../Card';
var getIcon = function (variant) {
    switch (variant) {
        case 'success':
            return { icon: muiSuccessFill, fill: 'success' };
        case 'error':
            return { icon: muiErrorFill, fill: 'error' };
        case 'warning':
            return { icon: muiErrorFill, fill: 'warning' };
        case 'info':
            return { icon: muiInfoFill, fill: 'plum' };
        case 'insights':
            return { icon: muiLightbulbInsights };
        default:
            return { icon: muiSuccessFill, fill: 'success' };
    }
};
var AlertInnerWrapper = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  align-items: flex-start;\n"], ["\n  display: flex;\n  flex-direction: row;\n  align-items: flex-start;\n"])));
var AlertIcon = styled(Icon)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  ", "\n  flex-shrink: 0;\n"], ["\n  ", "\n  flex-shrink: 0;\n"])), function (_a) {
    var _b = _a.theme, font = _b.font, baseIconSize = _b.baseIconSize, size = _a.size, fontSize = _a.fontSize;
    return "\n    margin-top: ".concat((parseInt(font.body[fontSize].lineHeight, 10) -
        baseIconSize[size || 'md']) /
        2, "px;\n  ");
});
var AlertContent = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: flex;\n  align-self: stretch;\n  justify-content: center;\n  flex-direction: column;\n  margin-left: ", ";\n"], ["\n  display: flex;\n  align-self: stretch;\n  justify-content: center;\n  flex-direction: column;\n  margin-left: ", ";\n"])), get.space('sm'));
var fontStyles = css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var font = _a.theme.font;
    return "\n    font-size: ".concat(font.body.sm.size, ";\n    line-height: ").concat(font.body.sm.lineHeight, ";\n  ");
});
var AlertHeading = styled(TextBody)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), fontStyles);
var AlertBody = styled(TextBody)(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), function (_a) {
    var withHeading = _a.withHeading, space = _a.theme.space;
    return withHeading
        ? css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n          padding-top: ", ";\n        "], ["\n          padding-top: ", ";\n        "])), space.xs) : css(templateObject_7 || (templateObject_7 = __makeTemplateObject([""], [""])));
}, fontStyles);
var AlertBase = React.forwardRef(function (_a, ref) {
    var variant = _a.variant, icon = _a.icon, focusOnMount = _a.focusOnMount, heading = _a.heading, body = _a.body, className = _a.className;
    var internalRef = useFocusOnMount();
    var focusRef = focusOnMount ? internalRef : ref;
    var fontSize = 'sm';
    return (React.createElement(AlertInnerWrapper, { className: className },
        React.createElement(AlertIcon, { icon: getIcon(variant).icon, fill: getIcon(variant).fill, size: "md", customTitle: icon === null || icon === void 0 ? void 0 : icon.customTitle, ariaHidden: icon === null || icon === void 0 ? void 0 : icon.ariaHidden, fontSize: fontSize }),
        React.createElement(AlertContent, null,
            React.createElement(React.Fragment, null,
                heading && (React.createElement(AlertHeading, { tag: "span", size: fontSize, tabIndex: -1, ref: focusRef }, heading)),
                body && (React.createElement(AlertBody, __assign({ tag: "span", size: fontSize, withHeading: !!heading }, (heading ? {} : { ref: focusRef, tabIndex: -1 })), body))))));
});
var StyledAlertBase = styled(AlertBase)(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n  background-color: transparent;\n"], ["\n  background-color: transparent;\n"
    // We don't destructure the props here in order to solve a weird tsc bug below.
])));
// We don't destructure the props here in order to solve a weird tsc bug below.
var Alert = React.forwardRef(function (props, ref) {
    var disableContainer = props.disableContainer, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleClose = props.handleClose, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    cancellable = props.cancellable, className = props.className, alertBaseProps = __rest(props, ["disableContainer", "handleClose", "cancellable", "className"]);
    if (disableContainer) {
        return (React.createElement(StyledAlertBase, __assign({ className: className }, alertBaseProps, { ref: ref })));
    }
    // Writing `props.cancellable` and `props.handleClose` instead of using the destructured values
    // seems redundant, but it actually fixes a weird issue that was causing the tsc to fail
    var cardProps = props.cancellable
        ? {
            closeButton: true,
            onClickCloseButton: props.handleClose,
            isFullScreenSmDown: false,
        }
        : { closeButton: false };
    return (React.createElement(Card, __assign({ className: className }, cardProps),
        React.createElement(AlertBase, __assign({}, alertBaseProps, { ref: ref }))));
});
var AlertWithMarginProps = styled(Alert)(margin);
export { AlertWithMarginProps as Alert, Alert as AlertForProps };
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9;
//# sourceMappingURL=Alert.js.map